// Waveform audio player for TTS/audiobook playback
// Uses ElevenLabs UI AudioScrubber for interactive waveform seek
import { useState, useRef, useEffect } from 'react'
import { AudioScrubber } from '@/components/ui/waveform'

interface AudioPlayerProps {
  src: string
  title?: string
  className?: string
  onPlay?: () => void
  onPause?: () => void
  onEnded?: () => void
}

export function AudioPlayer({ src, title, className, onPlay, onPause, onEnded }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTimeUpdate = () => setCurrentTime(audio.currentTime)
    const onLoadedMetadata = () => setDuration(audio.duration)
    const handleEnded = () => { setPlaying(false); onEnded?.() }

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  const toggle = () => {
    if (!audioRef.current) return
    if (playing) {
      audioRef.current.pause()
      onPause?.()
    } else {
      audioRef.current.play()
      onPlay?.()
    }
    setPlaying(!playing)
  }

  const handleSeek = (time: number) => {
    if (!audioRef.current) return
    audioRef.current.currentTime = time
    setCurrentTime(time)
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div className={`flex flex-col gap-3 ${className ?? ''}`}>
      {title && (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-200">{title}</p>
      )}

      <audio ref={audioRef} src={src} />

      {duration > 0 && (
        <AudioScrubber
          currentTime={currentTime}
          duration={duration}
          onSeek={handleSeek}
          height={56}
          barColor="#7c3aed"
          className="w-full rounded"
        />
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white text-sm transition-colors dark:bg-purple-500 dark:hover:bg-purple-600"
        >
          {playing ? 'Pause' : 'Play'}
        </button>
        {duration > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        )}
      </div>
    </div>
  )
}
